import { useMemo } from 'react'

import { buildFaqAnswerUrl } from '@karakuri-web/common/utils'
import {
  AnswerPageContext,
  Breadcrumb,
  FaqSettingMinimal,
  SearchParams,
} from '@karakuri-web/faq-type'

import {
  Answer,
  CopyLinkButton,
  GoToSearchButton,
  QAPanel,
  Question,
  SkillApi,
} from '../components'
import { useFaqData } from '../contexts/FaqDataContext'
import { useFaqRouting } from '../contexts/FaqRoutingContext'
import { useFaqSession } from '../contexts/FaqSessionContext'

/**
 * AnswerWidget用の設定インターフェース
 */
export interface AnswerWidgetSettings extends FaqSettingMinimal {
  siteUrl?: string | null
  pathPrefix?: string | null
  words?: {
    questionnaire?: {
      yesButtonLabel?: string | null
      noButtonLabel?: string | null
    } | null
  } | null
}

interface PageProps<D, C, S> {
  data: D
  pageContext: C
  path: string
  location?: { search: string }
  state?: S
}

export type Props<T extends AnswerWidgetSettings = AnswerWidgetSettings> = Pick<
  PageProps<{ settingYaml: T }, AnswerPageContext, SearchParams>,
  'data' | 'location' | 'pageContext' | 'path'
> & {
  breadcrumb: Breadcrumb
  skillApi: SkillApi
}

/**
 * 回答ウィジェット
 * FAQの質問と回答を表示する
 */
export function AnswerWidget<T extends AnswerWidgetSettings = AnswerWidgetSettings>({
  data,
  pageContext,
  breadcrumb,
  skillApi,
  location,
  path,
}: Props<T>) {
  const { navigate } = useFaqRouting()
  const faqData = useFaqData()
  const faqSession = useFaqSession()
  const { ask, contents, actions, questionnaireMessages } = pageContext || {}
  const { answers, cardId, operatorEscalationKeyword, karakuriWebRootUrl, info } = pageContext

  const faqUrl = useMemo(() => {
    if (!data.settingYaml.siteUrl || !cardId || cardId === 'new') return null
    return buildFaqAnswerUrl(data.settingYaml.siteUrl, data.settingYaml.pathPrefix, cardId)
  }, [data.settingYaml.siteUrl, data.settingYaml.pathPrefix, cardId])

  const handleClick = (to: string) => {
    navigate(to, {
      state: {
        hierarchies: [...breadcrumb.hierarchies, { title: pageContext.ask, link: path }],
      },
    })
  }
  const search = (location?.search || '').indexOf('search=true') > -1
  if (!contents || !cardId) {
    return null
  }
  function feedbackAction(action: 'yes' | 'no', callback: () => void) {
    const feedbackString = action === 'yes' ? 'ok' : 'ng'
    callback()
    faqData.sendFeedbackEvent(feedbackString)
  }

  const handleClickGoToSearchButton = () => {
    navigate(-1)
  }

  const wordsQuestionnaire = data?.settingYaml?.words?.questionnaire

  return (
    <QAPanel className="faq-answer">
      {faqUrl && <CopyLinkButton url={faqUrl} />}
      <Question q={ask || ''} info={info} />
      <Answer
        contents={contents}
        actions={actions}
        onClick={handleClick}
        cardId={cardId}
        pathPrefix={data.settingYaml.pathPrefix}
        answers={answers}
        operatorEscalationKeyword={operatorEscalationKeyword}
        karakuriWebRootUrl={karakuriWebRootUrl}
        messages={questionnaireMessages}
        wordsQuestionnaire={wordsQuestionnaire}
        info={info}
        contentPublishedAt={pageContext.contentPublishedAt}
        contentUpdatedAt={pageContext.contentUpdatedAt}
        session={{ value: faqSession.getValue() }}
        onFeedback={feedbackAction}
        skillApi={skillApi}
      />
      {search && <GoToSearchButton onClick={handleClickGoToSearchButton} />}
    </QAPanel>
  )
}
